import { useContext, useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Users,
  UserCheck,
  Clock,
  FileWarning,
  Eye,
  Check,
  X,
  Calendar,
  Loader2,
  RefreshCw,
} from "lucide-react";
import DatePickerField from "../../components/common/DatePickerField";
import { AuthContext } from "../Context/AuthContext";
import { isAdminLike, normalizeRole } from "../../rbac/access";
import { useToast } from "../Context/ToastContext";
import TdsStatCard from "./components/TdsStatCard";
import TdsStatusBadge from "./components/TdsStatusBadge";
import TdsField from "./components/TdsField";
import TdsFeedbackBanner from "./components/TdsFeedbackBanner";
import {
  TAX_REGIMES,
  formatINR,
  computeTaxableIncome,
  estimateAnnualTax,
} from "./tdsConstants";
import { loadTdsConfig, saveTdsConfig } from "./tdsStorage";
import {
  fetchEmployeeTaxRegister,
  parseTdsApiError,
  updateEmployeeDeclaration,
} from "./tdsApi";

function DeclarationModal({ employee, onClose, onAction, actionBusy, canApprove }) {
  if (!employee) return null;
  const ex = employee.exemptions || {};
  const totalExemptions = Object.values(ex).reduce((s, v) => s + (v || 0), 0);
  const taxable = employee.taxableIncome ?? computeTaxableIncome(employee.grossIncome, ex);
  const annualTax = employee.annualTax ?? estimateAnnualTax(taxable, employee.regime);
  const monthlyTds =
    employee.monthlyTds ?? (annualTax > 0 ? Math.round(annualTax / 12) : 0);

  return (
    <div className="tds-modal-overlay" onClick={onClose}>
      <motion.div
        className="tds-modal"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tds-modal-head">
          <div>
            <h3 className="font-bold text-slate-900">{employee.name}</h3>
            <p className="text-xs text-slate-500">
              {employee.code} · {employee.regime === "new" ? "New Regime" : "Old Regime"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={18} />
          </button>
        </div>
        <div className="tds-modal-body space-y-5">
          <div>
            <h4 className="text-sm font-bold text-violet-800 mb-3">Tax Declaration</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-xs text-slate-500 font-semibold">Section 80C</p>
                <p className="font-bold mt-1">
                  {formatINR(ex.section80C)}{" "}
                  <span className="text-xs font-normal text-slate-400">/ ₹1,50,000</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">PF, PPF, ELSS, LIC</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-xs text-slate-500 font-semibold">Section 80D</p>
                <p className="font-bold mt-1">{formatINR(ex.section80D)}</p>
                <p className="text-xs text-slate-500 mt-1">Medical insurance</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-xs text-slate-500 font-semibold">HRA Exemption</p>
                <p className="font-bold mt-1">{formatINR(ex.hra)}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-xs text-slate-500 font-semibold">Home Loan Interest</p>
                <p className="font-bold mt-1">{formatINR(ex.homeLoan)}</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold text-violet-800 mb-3">Tax Computation</h4>
            <div className="tds-computation-row">
              <span>Gross Income</span>
              <span className="font-semibold">{formatINR(employee.grossIncome)}</span>
            </div>
            <div className="tds-computation-row">
              <span>(−) Exemptions</span>
              <span className="font-semibold text-rose-600">− {formatINR(totalExemptions)}</span>
            </div>
            <div className="tds-computation-row">
              <span>= Taxable Income</span>
              <span className="font-semibold">{formatINR(taxable)}</span>
            </div>
            <div className="tds-computation-row">
              <span>Annual Tax (slab applied)</span>
              <span className="font-semibold">{formatINR(annualTax)}</span>
            </div>
            <div className="tds-computation-row tds-computation-row--total">
              <span>Monthly TDS</span>
              <span>{formatINR(monthlyTds)}</span>
            </div>
          </div>

          {canApprove &&
            (employee.declarationStatus === "Submitted" ||
              employee.declarationStatus === "Pending") && (
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                className="tds-btn tds-btn--primary flex-1"
                disabled={actionBusy}
                onClick={() => onAction(employee.id, "Approved")}
              >
                {actionBusy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                Approve
              </button>
              {employee.declarationStatus === "Submitted" && (
                <button
                  type="button"
                  className="tds-btn tds-btn--secondary flex-1"
                  disabled={actionBusy}
                  onClick={() => onAction(employee.id, "Rejected")}
                >
                  <X size={16} /> Reject
                </button>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function EmployeeTaxSetup() {
  const toast = useToast();
  const { role: authRole } = useContext(AuthContext);
  const canApprove = isAdminLike(normalizeRole(authRole));
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    taxable: 0,
    declarationsPending: 0,
    proofsPending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [config, setConfig] = useState(() => loadTdsConfig());
  const [feedback, setFeedback] = useState(null);

  const loadRegister = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchEmployeeTaxRegister();
      setEmployees(data.employees);
      setStats(data.stats);
    } catch (err) {
      const msg = await parseTdsApiError(err);
      setFeedback({ type: "error", message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadRegister();
  }, [loadRegister]);

  const filtered = useMemo(
    () =>
      employees.filter(
        (e) =>
          e.name.toLowerCase().includes(search.toLowerCase()) ||
          (e.code || "").toLowerCase().includes(search.toLowerCase())
      ),
    [employees, search]
  );

  const handleAction = async (id, status) => {
    const emp = employees.find((e) => e.id === id);
    setActionBusy(true);
    try {
      const updated = await updateEmployeeDeclaration(id, status);
      setEmployees((prev) => {
        const nextRows = prev.map((e) => (e.id === id ? updated : e));
        setStats({
          total: nextRows.length,
          taxable: nextRows.filter((e) => e.monthlyTds > 0).length,
          declarationsPending: nextRows.filter(
            (e) => e.declarationStatus === "Pending" || e.declarationStatus === "Submitted"
          ).length,
          proofsPending: nextRows.filter((e) => e.proofStatus === "Pending").length,
        });
        return nextRows;
      });
      const msg = `Declaration ${status.toLowerCase()} for ${emp?.name || "employee"}.`;
      setFeedback({ type: status === "Approved" ? "success" : "warn", message: msg });
      if (status === "Approved") toast.success(msg);
      else toast.error(msg);
      setSelected(null);
    } catch (err) {
      const msg = await parseTdsApiError(err);
      setFeedback({ type: "error", message: msg });
      toast.error(msg);
    } finally {
      setActionBusy(false);
    }
  };

  const patchWindow = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      windows: { ...prev.windows, [field]: value },
    }));
  };

  const saveWindows = () => {
    saveTdsConfig(config);
    const msg = "Declaration and proof submission windows saved successfully.";
    setFeedback({ type: "success", message: msg });
    toast.success(msg);
  };

  return (
    <div className="tds-page-content">
      <TdsFeedbackBanner
        message={feedback?.message}
        type={feedback?.type}
        onClose={() => setFeedback(null)}
      />
      <div className="tds-stat-grid">
        <TdsStatCard label="Total Employees" value={loading ? "…" : stats.total} icon={Users} bg="#f5f3ff" color="#7c3aed" />
        <TdsStatCard label="Taxable Employees" value={loading ? "…" : stats.taxable} icon={UserCheck} bg="#ecfdf5" color="#059669" />
        <TdsStatCard label="Declarations Pending" value={loading ? "…" : stats.declarationsPending} icon={Clock} bg="#fffbeb" color="#d97706" />
        <TdsStatCard label="Proofs Pending" value={loading ? "…" : stats.proofsPending} icon={FileWarning} bg="#fff1f2" color="#e11d48" />
      </div>

      <div className="tds-grid-2 gap-5 mb-5">
        <section className="tds-panel">
          <div className="tds-panel-head">
            <div>
              <h2 className="tds-panel-title">
                <Calendar size={16} className="text-violet-600" />
                Declaration Window
              </h2>
              <p className="tds-panel-sub">Employees can submit declarations only during this period</p>
            </div>
          </div>
          <div className="tds-panel-body tds-grid-2">
            <TdsField label="Start Date">
              <DatePickerField hideLabel value={config.windows.declarationStart} onChange={(v) => patchWindow("declarationStart", v)} />
            </TdsField>
            <TdsField label="End Date">
              <DatePickerField hideLabel value={config.windows.declarationEnd} onChange={(v) => patchWindow("declarationEnd", v)} />
            </TdsField>
          </div>
        </section>

        <section className="tds-panel">
          <div className="tds-panel-head">
            <div>
              <h2 className="tds-panel-title">
                <Calendar size={16} className="text-emerald-600" />
                Proof Submission Window
              </h2>
              <p className="tds-panel-sub">Period for employees to upload investment proofs</p>
            </div>
          </div>
          <div className="tds-panel-body tds-grid-2">
            <TdsField label="Proof Upload Start">
              <DatePickerField hideLabel value={config.windows.proofStart} onChange={(v) => patchWindow("proofStart", v)} />
            </TdsField>
            <TdsField label="Proof Upload End">
              <DatePickerField hideLabel value={config.windows.proofEnd} onChange={(v) => patchWindow("proofEnd", v)} />
            </TdsField>
          </div>
        </section>
      </div>

      <div className="flex justify-end mb-5">
        <button type="button" className="tds-btn tds-btn--primary" onClick={saveWindows}>
          Save Declaration Windows
        </button>
      </div>

      <section className="tds-panel">
        <div className="tds-panel-head">
          <div>
            <h2 className="tds-panel-title">Employee Tax Register</h2>
            <p className="tds-panel-sub">Live employees from your organisation — TDS from CTC and tax metadata</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="tds-btn tds-btn--secondary tds-btn--sm"
              onClick={loadRegister}
              disabled={loading}
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Refresh
            </button>
            <input
              type="search"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg w-48"
            />
          </div>
        </div>
        <div className="tds-panel-body" style={{ padding: 0 }}>
          <div className="tds-table-wrap" style={{ border: "none", borderRadius: 0 }}>
            <table className="tds-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Regime</th>
                  <th>Declaration</th>
                  <th>Proof</th>
                  <th>Monthly TDS</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-slate-500">
                      <Loader2 size={20} className="animate-spin inline-block mr-2" />
                      Loading employees…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-slate-500">
                      No employees found. Add employees or adjust your search.
                    </td>
                  </tr>
                ) : (
                  filtered.map((emp) => (
                    <tr key={emp.id}>
                      <td>
                        <div className="font-semibold">{emp.name}</div>
                        <div className="text-xs text-slate-500">{emp.code}</div>
                      </td>
                      <td>
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-violet-50 text-violet-700">
                          {TAX_REGIMES.find((r) => r.value === emp.regime)?.label || emp.regime}
                        </span>
                      </td>
                      <td><TdsStatusBadge status={emp.declarationStatus} /></td>
                      <td><TdsStatusBadge status={emp.proofStatus} /></td>
                      <td className="font-bold text-violet-800">{formatINR(emp.monthlyTds)}</td>
                      <td>
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            className="tds-btn tds-btn--secondary tds-btn--sm"
                            onClick={() => setSelected(emp)}
                          >
                            <Eye size={14} /> View
                          </button>
                          {canApprove &&
                            (emp.declarationStatus === "Pending" ||
                              emp.declarationStatus === "Submitted") && (
                            <button
                              type="button"
                              className="tds-btn tds-btn--primary tds-btn--sm"
                              disabled={actionBusy}
                              onClick={() => handleAction(emp.id, "Approved")}
                            >
                              <Check size={14} /> Approve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {selected && (
        <DeclarationModal
          employee={selected}
          onClose={() => setSelected(null)}
          onAction={handleAction}
          actionBusy={actionBusy}
          canApprove={canApprove}
        />
      )}
    </div>
  );
}
